// App.js
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import LoadingSkeleton from './components/ui/LoadingSkeleton';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { setupGlobalErrorHandlers, setGlobalErrorHandler } from './utils/globalErrorHandler';
import AuthProvider, { useAuth } from './components/auth/AuthProvider';
import { isNativeApp } from './utils/platform';
import { setupDeeplinks } from './plugins/native-bridge';
import { setupNotificationListener, getNotificationPrefs, getScheduledStory, scheduleBedtimeNotification } from './services/notificationService';
import { trackPageView } from './services/analytics';
import './styles/design-system.css';
import './styles/animations.css';
import './App.css';

// Lazy load components for better performance
const Homepage = React.lazy(() => import('./components/Homepage'));
const StoryCreationFlow = React.lazy(() => import('./components/story/StoryCreationFlow'));
const MyStoriesDashboard = React.lazy(() => import('./components/dashboard/MyStoriesDashboard'));
const StoryViewer = React.lazy(() => import('./components/story/StoryViewer'));
const UserProfile = React.lazy(() => import('./components/profile/UserProfile'));
const LoginPage = React.lazy(() => import('./components/auth/LoginPage'));
const VerifyEmailPage = React.lazy(() => import('./components/auth/VerifyEmailPage'));
const OAuthCallback = React.lazy(() => import('./components/auth/OAuthCallback'));
const ProtectedRoute = React.lazy(() => import('./components/auth/ProtectedRoute'));
const PrivacyPage = React.lazy(() => import('./components/legal/PrivacyPage'));
const TermsPage = React.lazy(() => import('./components/legal/TermsPage'));
const DataDeletionPage = React.lazy(() => import('./components/legal/DataDeletionPage'));
const RefundPolicyPage = React.lazy(() => import('./components/legal/RefundPolicyPage'));
const PricingPage = React.lazy(() => import('./components/pricing/PricingPage'));
const UnsubscribePage = React.lazy(() => import('./components/email/UnsubscribePage'));
const ContactPage = React.lazy(() => import('./components/legal/ContactPage'));
const AboutPage = React.lazy(() => import('./components/legal/AboutPage'));

// Loading fallback component
const PageLoader = () => (
  <div style={{ padding: '50px 0', background: 'var(--color-bg-secondary)', minHeight: '100vh' }}>
    <div className="container">
      <LoadingSkeleton type="story-page" count={1} />
    </div>
  </div>
);

// Page view tracker — must be inside <Router>
// Also handles scroll-to-top on every route change (holistic solution)
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

// Inner App component that has access to Toast context
function AppContent() {
  const { showError } = useToast();
  const { isAuthenticated, signOut } = useAuth();

  useEffect(() => {
    setupGlobalErrorHandlers();
    setGlobalErrorHandler(showError);
    // Capacitor deep link handling (Universal Links / custom scheme)
    setupDeeplinks();

    // Bedtime story notification tap handler — uses window.location because
    // this component is outside <Router>, and notifications can arrive during cold start
    setupNotificationListener((path) => {
      window.location.href = path;
    });

    // Hide splash screen — single call, React has already mounted
    if (isNativeApp() && window.Capacitor?.Plugins?.SplashScreen) {
      window.Capacitor.Plugins.SplashScreen.hide();
    }
  }, [showError]);

  // Reschedule bedtime notification if enabled (handles app restart/force-quit)
  useEffect(() => {
    if (!isNativeApp()) return;
    const scheduled = getScheduledStory();
    const prefs = getNotificationPrefs();
    if (scheduled && prefs.enabled) {
      scheduleBedtimeNotification({
        hour: prefs.hour ?? 20,
        minute: prefs.minute ?? 0,
        title: 'Story Time! \u{1F4DA}',
        body: `${scheduled.childName ? scheduled.childName + "'s " : ''}${scheduled.themeLabel || ''} adventure is ready!`,
      });
    }
  }, []);

  const handleAuthSuccess = () => {
    let redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/create';
    redirectPath = redirectPath.replace(/\/$/, '') || '/';
    sessionStorage.removeItem('redirectAfterLogin');
    window.location.href = redirectPath;
  };

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <Router>
      <PageViewTracker />
      <div className="App">

        <div className="page-transition">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage onAuthSuccess={handleAuthSuccess} />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/stories/shared/:shareToken" element={<StoryViewer isSharedView={true} />} />
              <Route path="/homepage" element={<Homepage />} />
              <Route path="/create" element={<StoryCreationFlow />} />
              <Route path="/create/:draftId" element={<StoryCreationFlow />} />
              <Route path="/create/:draftId/:step" element={<StoryCreationFlow />} />
              <Route path="/my-stories" element={<ProtectedRoute><MyStoriesDashboard /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
              <Route path="/story/:storyId" element={<ProtectedRoute><StoryViewer /></ProtectedRoute>} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/delete" element={<DataDeletionPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/" element={<Homepage />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </Router>
  );
}

// Main App component with providers
function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
